
export enum UserRole {
  DENTIST = 'DENTIST',
  TECHNICIAN = 'TECHNICIAN',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR'
}

export enum OrderStatus {
  PENDING = 'PENDING',
  DESIGNING = 'DESIGNING',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  MILLING = 'MILLING',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  IN_TRY_IN = 'IN_TRY_IN',
  READY = 'READY',
  RECEIVED = 'RECEIVED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED'
}

export enum Urgency {
  NORMAL = 'Normal',
  URGENT = 'Urgent'
}

export interface SystemSettings {
  id: string;
  pmma_price: number;
  urgent_rate: number;
  currency: string;
  logoUrl?: string;
  notification_time?: number; // Hour of day (0-23)
}

export interface ClinicBranch {
  id: string;
  doctorId: string;
  name: string;
  location?: string;
  createdAt: string;
}

export interface PricePlan {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface PricePlanItem {
  id: string;
  planId: string;
  serviceId: string;
  price: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  clinicName?: string;
  phone?: string;
  secondaryPhones?: string[]; // New field
  location?: string;
  onboardingComplete: boolean;
  balance: number;
  isTrusted: boolean;
  commissionFixed?: number;
  commissionPercentage?: number;
  isDefaultDesigner?: boolean;
  parentId?: string;
  canSeeFinancials?: boolean;
  branchId?: string;
  branchName?: string;
  pricePlanId?: string;
}

export interface ClinicInvite {
  id: string;
  email: string;
  name: string;
  canSeeFinancials: boolean;
  branchId?: string;
  status: 'PENDING' | 'ACCEPTED';
  createdAt: string;
}

export interface DesignerServiceRate {
  id: string;
  designerId: string;
  serviceId: string;
  rate: number;
}

export interface MillingServiceOffering {
  serviceId: string;
  cost: number;
}

export interface MillingCenter {
  id: string;
  name: string;
  whatsappNumber: string;
  servicesList: string;
  pricingInfo: string;
  offerings?: MillingServiceOffering[];
}

export interface TopUpRequest {
  id: string;
  doctorId: string;
  doctorName?: string;
  amount: number;
  proofUrl: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  orderId?: string;
  createdAt: string;
}

export interface Transaction {
  id: string;
  profileId?: string;
  amount: number;
  type: 'TOP_UP' | 'ORDER_PAYMENT' | 'ADJUSTMENT' | 'DESIGNER_PAYOUT' | 'DESIGNER_COMMISSION';
  referenceId?: string;
  description: string;
  createdAt: string;
}

export interface OrderFile {
  id: string;
  name: string;
  url: string;
  uploadedAt: string;
  type: 'scan' | 'design' | 'invoice' | 'other';
  size?: number;
}

export interface ServiceItem {
  id: string;
  name: string;
  price: number;
  color?: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  services: ServiceItem[];
}

export interface OrderItem {
  serviceId: string;
  serviceName: string;
  teeth: string[];
  price: number;
  details?: string;
}

export interface DesignFiles {
  htmlPreview?: string;
  productionZip?: string;
}

export interface Order {
  id: string;
  patientName: string;
  doctorName: string;
  doctorId: string;
  technicianId?: string;
  millingCenterId?: string;
  branchId?: string;
  branchName?: string;
  items: OrderItem[];
  toothNumber: string;
  shade: string;
  status: OrderStatus;
  urgency: Urgency;
  createdAt: string;
  dueDate: string;
  notes: string;
  files: OrderFile[];
  price: number;
  manualCost: number;
  requiresDesignCheck: boolean;
  isPMMA: boolean;
  designFiles?: DesignFiles;
  needsContact?: boolean;
  type?: string;
  isPaid?: boolean;
}

export interface Message {
  id: string;
  orderId: string;
  senderId: string;
  senderName?: string;
  content: string;
  createdAt: string;
}

export interface TimelineEvent {
  id: string;
  orderId: string;
  title: string;
  description: string;
  status?: string;
  createdAt: string;
}

export interface FileUploadProgress {
  id: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  orderId?: string;
  fileObj?: File;
}

export interface AppNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  isRead: boolean;
  link?: string;
  createdAt: string;
  type: 'info' | 'success' | 'warning' | 'error';
}
